if (!customElements.get('h2o-delivery-pill')) {
  class H2ODeliveryPill extends HTMLElement {
    connectedCallback() {
      this.textElement = this.querySelector('[data-delivery-pill-text]');
      this.variantEligibility = this.getVariantEligibility();
      this.onVariantChange = this.handleVariantChange.bind(this);
      this.variantInput = document.querySelector(
        `#product-form-${this.dataset.sectionId} input[name="id"]`
      );

      if (this.variantInput) {
        this.variantInput.addEventListener('change', this.onVariantChange);
        this.updateEligibility(this.variantInput.value);
      } else {
        this.render();
      }
    }

    disconnectedCallback() {
      this.variantInput?.removeEventListener('change', this.onVariantChange);
    }

    getVariantEligibility() {
      const dataElement = this.querySelector('[data-delivery-variant-eligibility]');

      try {
        return JSON.parse(dataElement?.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    handleVariantChange(event) {
      this.updateEligibility(event.target.value);
    }

    updateEligibility(variantId) {
      const variant = this.variantEligibility.find(
        (item) => String(item.id) === String(variantId)
      );

      this.supplierBacked = variant?.supplierBacked === true;
      this.toggleAttribute('data-supplier-backed', this.supplierBacked);
      this.closest('.product__info-container')?.toggleAttribute(
        'data-h2o-supplier-backed',
        this.supplierBacked
      );
      this.hidden = !variant?.eligible;

      if (!this.hidden) this.render();
    }

    render(now = new Date()) {
      if (!this.textElement) return;

      if (this.supplierBacked) {
        this.textElement.textContent = this.dataset.supplierDispatchLabel;
        this.setAttribute('data-ready', '');
        return;
      }

      const estimate = H2ODeliveryPill.calculate({
        now,
        timeZone: this.dataset.timeZone || 'Australia/Brisbane',
        cutoffTime: this.dataset.cutoffTime || '12:00',
        transitDays: Number.parseInt(this.dataset.transitDays, 10) || 2,
      });
      const dispatchLabel = estimate.dispatchesToday
        ? this.dataset.dispatchesTodayLabel
        : this.dataset.dispatchesNextLabel;

      const deliveryLabel = document.createTextNode(
        `${this.dataset.estimatedDeliveryLabel} `
      );
      const deliveryDate = document.createElement('span');
      const dispatchText = document.createTextNode(` • ${dispatchLabel}`);

      deliveryDate.className = 'h2o-delivery-pill__date';
      deliveryDate.textContent = estimate.formattedDeliveryDate;
      this.textElement.replaceChildren(deliveryLabel, deliveryDate, dispatchText);
      this.setAttribute('data-ready', '');
    }

    static calculate({ now, timeZone, cutoffTime, transitDays }) {
      const current = H2ODeliveryPill.getZonedDateParts(now, timeZone);
      const cutoffMinutes = H2ODeliveryPill.parseCutoff(cutoffTime);
      const currentMinutes = current.hour * 60 + current.minute;
      const dispatchesToday =
        H2ODeliveryPill.isBusinessDay(current) && currentMinutes < cutoffMinutes;
      const dispatchDate = dispatchesToday
        ? current
        : H2ODeliveryPill.nextBusinessDay(current);
      const deliveryDate = H2ODeliveryPill.addBusinessDays(
        dispatchDate,
        Math.max(1, transitDays)
      );

      return {
        dispatchDate,
        deliveryDate,
        dispatchesToday,
        formattedDeliveryDate: H2ODeliveryPill.formatDate(deliveryDate),
      };
    }

    static getZonedDateParts(date, timeZone) {
      const parts = new Intl.DateTimeFormat('en-AU', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(date);
      const values = Object.fromEntries(
        parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value])
      );

      return {
        year: Number.parseInt(values.year, 10),
        month: Number.parseInt(values.month, 10),
        day: Number.parseInt(values.day, 10),
        hour: Number.parseInt(values.hour, 10),
        minute: Number.parseInt(values.minute, 10),
      };
    }

    static parseCutoff(cutoffTime) {
      const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(cutoffTime);

      if (!match) return 12 * 60;

      return Number.parseInt(match[1], 10) * 60 + Number.parseInt(match[2], 10);
    }

    static isBusinessDay(date) {
      const day = new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
      return day !== 0 && day !== 6;
    }

    static nextBusinessDay(date) {
      let next = H2ODeliveryPill.addCalendarDays(date, 1);

      while (!H2ODeliveryPill.isBusinessDay(next)) {
        next = H2ODeliveryPill.addCalendarDays(next, 1);
      }

      return next;
    }

    static addBusinessDays(date, days) {
      let result = { ...date };
      let remaining = days;

      while (remaining > 0) {
        result = H2ODeliveryPill.addCalendarDays(result, 1);
        if (H2ODeliveryPill.isBusinessDay(result)) remaining -= 1;
      }

      return result;
    }

    static addCalendarDays(date, days) {
      const value = new Date(Date.UTC(date.year, date.month - 1, date.day + days));

      return {
        year: value.getUTCFullYear(),
        month: value.getUTCMonth() + 1,
        day: value.getUTCDate(),
        hour: date.hour,
        minute: date.minute,
      };
    }

    static formatDate(date) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'UTC',
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      }).formatToParts(new Date(Date.UTC(date.year, date.month - 1, date.day)));
      const values = Object.fromEntries(
        parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value])
      );

      return `${values.weekday}, ${values.day} ${values.month}`;
    }
  }

  customElements.define('h2o-delivery-pill', H2ODeliveryPill);
}
